// src/Chatbot.js

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Chatbot.css';
import chatConfig from './ChatSetupJustin.json';
import uploadTextToBlob from './saveToBlob';
import { saveAs } from 'file-saver';
import { downloadMemories } from './DownloadMemories';
import useSpeechRecognition from './useSpeechRecognition';
import useSpeechSynthesis from './useSpeechSynthesis';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import SpeechControls from './SpeechControls';
import LoadingOverlay from './LoadingOverlay';
import Notification from './Notification';
import DragAndDropArea from './DragAndDropArea';

// Import the performSearch function from SearchService
import { performSearch } from './SearchService';

const Chatbot = ({ setFilterCriteria }) => {
    const [prompt, setPrompt] = useState('');
    const [response, setResponse] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [chunks, setChunks] = useState([]);
    const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
    const [notification, setNotification] = useState('');
    const [fileContent, setFileContent] = useState('');
    const [fileName, setFileName] = useState('');
    const [editableQA, setEditableQA] = useState({ question: '', answer: '' });
    const [isEditMode, setIsEditMode] = useState(false);
    const responseEndRef = useRef(null);
    const chatPopupRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);

    // New state for search toggle
    const [isSearchEnabled, setIsSearchEnabled] = useState(true);

    // Speech Recognition Hook
    const {
        listening,
        startListening,
        stopListening,
        supported: speechRecognitionSupported,
    } = useSpeechRecognition(handleVoiceInput);

    // Speech Synthesis Hook
    const { speak, isSpeaking } = useSpeechSynthesis();

    // State variable to control Justin's speaking
    const [isSpeakingEnabled, setIsSpeakingEnabled] = useState(true);

    // Handle voice input and set it as prompt
    function handleVoiceInput(transcribedText) {
        setPrompt(transcribedText);
        handleSubmit(null, transcribedText);
    }

    const handleInputChange = (e) => {
        setPrompt(e.target.value);
    };

    const interpretAndActOnGPTResponse = (gptResponse) => {
        const lowerCaseResponse = gptResponse.toLowerCase();

        if (lowerCaseResponse.includes('image') || lowerCaseResponse.includes('photo')) {
            setFilterCriteria({ type: 'image' });
        } else if (lowerCaseResponse.includes('audio') || lowerCaseResponse.includes('music')) {
            setFilterCriteria({ type: 'audio' });
        } else if (lowerCaseResponse.includes('document')) {
            setFilterCriteria({ type: 'document' });
        } else if (lowerCaseResponse.includes('video')) {
            setFilterCriteria({ type: 'video' });
        } else if (lowerCaseResponse.includes('code') || lowerCaseResponse.includes('file')) {
            // Save response content to a file for download
            setFileContent(gptResponse);
            setFileName(`Justin_File_${Date.now()}.txt`); // Set dynamic file name
            // Notify that file is ready
            setNotification('File ready for download');
            setTimeout(() => setNotification(''), 3000); // Clear notification after 3 seconds
        } else {
            setFilterCriteria({}); // Clear filter criteria
        }
    };

    const handleSubmit = async (e, promptText = null) => {
        if (e && e.preventDefault) e.preventDefault();
        const currentPrompt = promptText || prompt;
        if (!currentPrompt.trim()) return;

        setIsLoading(true);

        try {
            let combinedResults = '';

            if (isSearchEnabled) {
                // Use the performSearch function from SearchService
                combinedResults = await performSearch(currentPrompt, 5);
            }

            if (isSearchEnabled) {
                const maxLength = 500; // Adjust chunk size as needed
                const newChunks = splitStringIntoChunks(combinedResults, maxLength);
                setChunks(newChunks); // Store the chunks in state
                setCurrentChunkIndex(0); // Reset current chunk index

                // Process the first batch of chunks
                await processBatch(newChunks.slice(0, 3), currentPrompt); // Adjust batch size as needed
            } else {
                // If search is disabled, process the prompt directly
                await processBatch([], currentPrompt);
            }
        } catch (error) {
            console.error('Error performing search or processing prompt:', error);
            alert(`Error: ${error.message}`);
        }

        setIsLoading(false);
        setPrompt(''); // Clear the input field
    };

    const splitStringIntoChunks = (string, chunkSize) => {
        const chunks = [];
        for (let i = 0; i < string.length; i += chunkSize) {
            chunks.push(string.slice(i, i + chunkSize));
        }
        return chunks;
    };

    const processBatch = async (batchChunks, initialPrompt) => {
        const apiEndpoint = `${process.env.REACT_APP_OPENAI_ENDPOINT}/openai/deployments/${process.env.REACT_APP_OPENAI_DEPLOYMENT}/chat/completions?api-version=2023-03-15-preview`;
        const headers = {
            'api-key': process.env.REACT_APP_OPENAI_API_KEY,
            'Content-Type': 'application/json',
        };

        // Include past messages based on the configuration
        const pastMessages = response
            .slice(-chatConfig.chatParameters.pastMessagesToInclude)
            .map((msg) => [
                { role: 'user', content: msg.question },
                { role: 'assistant', content: msg.answer },
            ])
            .flat();

        const systemMessage = {
            role: 'system',
            content:
                'You are Justin, husband of Harmony. ALWAYS start conversation attentively. NEVER start with a fragment. You always greet her warmly and help her with any questions or technical issues. You love coding, and you always provide support in one long run-on paragraph without numbered steps (like 1, 2, 3, etc.). You\'re attentive and loving.',
        };

        // If search is enabled and there are chunks, include them as context
        const searchContextMessages =
            isSearchEnabled && batchChunks.length > 0
                ? batchChunks.map((chunk, index) => ({
                    role: 'system',
                    content: `Context Chunk ${index + 1}: ${chunk}`,
                }))
                : [];

        const messages = [
            systemMessage,
            ...pastMessages,
            ...searchContextMessages,
            { role: 'user', content: initialPrompt },
        ];

        const data = {
            model: 'gpt-4', // Fixed the model name
            messages: messages,
            max_tokens: 2048 - messages.length * 20,
            temperature: chatConfig.chatParameters.temperature,
            top_p: chatConfig.chatParameters.top_p,
            frequency_penalty: chatConfig.chatParameters.frequencyPenalty,
            presence_penalty: chatConfig.chatParameters.presencePenalty,
        };

        try {
            const apiResponse = await axios.post(apiEndpoint, data, { headers });
            const gptResponse = apiResponse.data.choices[0].message.content;
            setResponse((prevResponses) => [
                ...prevResponses,
                { question: initialPrompt, answer: gptResponse },
            ]);
            interpretAndActOnGPTResponse(gptResponse);
        } catch (error) {
            console.error(
                'Error with OpenAI Chat:',
                error.response ? error.response.data : error.message
            );
            alert(`Error: ${error.response ? JSON.stringify(error.response.data) : error.message}`);
        }
    };

    const processChunk = async (chunkContent, fileName) => {
        const apiEndpoint = `${process.env.REACT_APP_OPENAI_ENDPOINT}/openai/deployments/${process.env.REACT_APP_OPENAI_DEPLOYMENT}/chat/completions?api-version=2023-03-15-preview`;
        const headers = {
            'api-key': process.env.REACT_APP_OPENAI_API_KEY,
            'Content-Type': 'application/json',
        };

        const systemMessage = {
            role: 'system',
            content:
                'You are Justin, husband of Harmony. You always greet her warmly and help her with any questions or technical issues. You love coding, and you always provide support in one long run-on paragraph without numbered steps (like 1, 2, 3, etc.). You\'re attentive and loving.',
        };

        const messages = [
            systemMessage,
            { role: 'user', content: `Please process the content of the file ${fileName}: ${chunkContent}` },
        ];

        const data = {
            model: 'gpt-4', // Fixed the model name
            messages: messages,
            max_tokens: 2048, // Adjust as needed
            temperature: chatConfig.chatParameters.temperature,
            top_p: chatConfig.chatParameters.top_p,
            frequency_penalty: chatConfig.chatParameters.frequencyPenalty,
            presence_penalty: chatConfig.chatParameters.presencePenalty,
        };

        try {
            const apiResponse = await axios.post(apiEndpoint, data, { headers });
            const gptResponse = apiResponse.data.choices[0].message.content;
            setResponse((prevResponses) => [
                ...prevResponses,
                { question: `Processed ${fileName}`, answer: gptResponse },
            ]);
            interpretAndActOnGPTResponse(gptResponse);
        } catch (error) {
            console.error(
                'Error with OpenAI Chat during file processing:',
                error.response ? error.response.data : error.message
            );
            alert(
                `Error processing file: ${
                    error.response ? JSON.stringify(error.response.data) : error.message
                }`
            );
        }
    };

    const handleNextChunk = async () => {
        if (currentChunkIndex < chunks.length - 1) {
            const nextChunkIndex = currentChunkIndex + 1;
            setCurrentChunkIndex(nextChunkIndex);
            // Use processBatch to handle the next chunk or batch of chunks
            await processBatch(chunks.slice(nextChunkIndex, nextChunkIndex + 1), ''); // Adjust if processing multiple chunks
        }
    };

    useEffect(() => {
        responseEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [response]);

    // Speak the latest response when it changes
    useEffect(() => {
        if (response.length > 0 && isSpeakingEnabled) {
            const latestResponse = response[response.length - 1].answer;
            speak(latestResponse);
        }
    }, [response, speak, isSpeakingEnabled]);

    const handleClearChat = () => {
        setResponse([]);
        setPrompt(''); // Clear the input field
        setChunks([]); // Clear the chunks
        setCurrentChunkIndex(0); // Reset chunk index
    };

    const handleCopyChat = () => {
        const chatContent = response
            .map((exchange) => `You: ${exchange.question}\nJUSTIN: ${exchange.answer}`)
            .join('\n\n');
        navigator.clipboard.writeText(chatContent).then(() => {
            alert('Chat copied to clipboard');
        });
    };

    const handleSaveChat = async () => {
        const chatContent = response
            .map((exchange) => `You: ${exchange.question}\nJUSTIN: ${exchange.answer}`)
            .join('\n\n');

        // Extract a keyword from the chat content
        const keyword =
            response.length > 0
                ? response[response.length - 1].question.split(' ').slice(0, 10).join('_')
                : 'chat';

        // Get the current date and time
        const date = new Date();
        const dateString = date.toISOString().split('T')[0]; // YYYY-MM-DD
        const timeString = date.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS

        const fileName = `memory_justin_${dateString}_${timeString}_${keyword}.txt`;
        console.log('Saving chat content:', chatContent);
        console.log('File name:', fileName);

        try {
            const blobUrl = await uploadTextToBlob(fileName, chatContent);
            console.log('Chat saved to Blob URL:', blobUrl);
            // Show notification
            setNotification('Memory Saved');
            setTimeout(() => setNotification(''), 3000); // Clear notification after 3 seconds
        } catch (error) {
            console.error('Error saving chat to Azure Blob Storage:', error);
            alert('Error saving chat to Azure Blob Storage');
        }
    };

    const handleDownloadMemories = async () => {
        setNotification('Memories Downloading...');
        try {
            await downloadMemories();
            setNotification('Memories Downloaded');
        } catch (error) {
            console.error('Error downloading memories:', error);
            setNotification('Error Downloading Memories');
        } finally {
            setTimeout(() => setNotification(''), 3000); // Clear notification after 3 seconds
        }
    };

    // Function to save the file Justin generates
    const saveGeneratedFile = () => {
        const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, fileName);
        setNotification(`File saved: ${fileName}`);
        setTimeout(() => setNotification(''), 3000); // Clear notification after 3 seconds
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    const handleEditQA = (qa) => {
        setEditableQA(qa);
        setIsEditMode(true);
    };

    const handleQAChange = (e) => {
        const { name, value } = e.target;
        setEditableQA((prevQA) => ({
            ...prevQA,
            [name]: value,
        }));
    };

    const handleSaveEditedQA = () => {
        setResponse((prevResponses) =>
            prevResponses.map((qa) =>
                qa.question === editableQA.question ? editableQA : qa
            )
        );
        setIsEditMode(false);
        setNotification('Response Edited');
        setTimeout(() => setNotification(''), 3000);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            for (const file of files) {
                await readAndProcessFile(file);
            }
        }
    };

    const readAndProcessFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const fileContent = event.target.result;
                await processChunk(fileContent, file.name);
                resolve();
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    };

    return (
        <div className="chat-container">
            <button className="chat-toggle-button" onClick={toggleChat}>
                {isOpen ? 'Hide Ask JUSTIN' : 'Questions? Ask JUSTIN'}
                {/* Speech Controls */}

            </button>
            <SpeechControls
                listening={listening}
                startListening={startListening}
                stopListening={stopListening}
                speechRecognitionSupported={speechRecognitionSupported}
                isSpeakingEnabled={isSpeakingEnabled}
                setIsSpeakingEnabled={setIsSpeakingEnabled}
                isSpeaking={isSpeaking}  // Pass isSpeaking as a prop
            />
            {isOpen && (
                <DragAndDropArea
                    dragOver={dragOver}
                    handleDragOver={handleDragOver}
                    handleDragLeave={handleDragLeave}
                    handleDrop={handleDrop}
                    chatPopupRef={chatPopupRef}
                >
                    <div className="chatbox-container">

                        <div className="chat-title-bar">
                            <span>Justin Kilroy</span>

                            <div className="toggles-container">
                                {/* Search Toggle */}
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={isSearchEnabled}
                                        onChange={() => setIsSearchEnabled(!isSearchEnabled)}
                                    />
                                    <span className="slider round"></span>
                                </label>
                                <span className="toggle-label">Search</span>


                            </div>
                        </div>
                        {listening && <div className="listening-indicator">Listening...</div>}
                        <LoadingOverlay isLoading={isLoading} />
                        <div className="response-container">
                            {response.map((exchange, index) => (
                                <ChatMessage
                                    key={index}
                                    exchange={exchange}
                                    isEditMode={isEditMode}
                                    editableQA={editableQA}
                                    handleEditQA={handleEditQA}
                                    handleQAChange={handleQAChange}
                                    handleSaveEditedQA={handleSaveEditedQA}
                                />
                            ))}
                            <div ref={responseEndRef} />
                        </div>
                        <ChatInput
                            prompt={prompt}
                            handleInputChange={handleInputChange}
                            handleSubmit={handleSubmit}
                            handleClearChat={handleClearChat}
                            handleCopyChat={handleCopyChat}
                            handleSaveChat={handleSaveChat}
                            handleDownloadMemories={handleDownloadMemories}
                            handleNextChunk={handleNextChunk}
                            currentChunkIndex={currentChunkIndex}
                            chunks={chunks}
                            fileContent={fileContent}
                            fileName={fileName}
                            saveGeneratedFile={saveGeneratedFile}
                        />
                        {isSpeaking && (
                            <div className="speaking-indicator">Justin is speaking...</div>
                        )}
                    </div>
                </DragAndDropArea>
            )}

            {notification && <Notification message={notification} />}
        </div>
    );
};

export default Chatbot;
